import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

/**
 * Modal nur für kurze, abgeschlossene Aufgaben (Bewerten, Preis, Bestätigen, Schnellanlage).
 * Umfangreiche Formulare sind eigene Seiten. Standardbreite schmal, einspaltig.
 */
export function Modal({
  title,
  subtitle,
  children,
  footer,
  onClose,
  size = 'narrow',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: 'wide' | 'narrow' | 'default';
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const focusables = () => Array.from(el?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? []).filter((f) => !f.hasAttribute('disabled'));
    const first = focusables().find((f) => !f.closest('.mh'));
    (first ?? focusables()[0])?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab') {
        const f = focusables();
        if (!f.length) return;
        const i = f.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey && i <= 0) {
          e.preventDefault();
          f[f.length - 1]!.focus();
        } else if (!e.shiftKey && i === f.length - 1) {
          e.preventDefault();
          f[0]!.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [onClose]);
  return createPortal(
    <div className="modal-bg" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size === 'default' ? '' : size}`} role="dialog" aria-modal="true" ref={ref}>
        <div className="mh">
          <div>
            <h2>{title}</h2>
            {subtitle && <div className="mut small">{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Schließen" type="button">
            <Icon name="x" />
          </button>
        </div>
        <div className="mb">{children}</div>
        {footer !== null && (
          <div className="mf">
            {footer ?? (
              <button className="btn" onClick={onClose} type="button">
                Schließen
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  title,
  text,
  confirmLabel = 'Bestätigen',
  danger,
  onConfirm,
  onClose,
  busy,
}: {
  title: string;
  text: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className={`btn ${danger ? 'danger' : 'primary'}`} type="button" onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </button>
        </>
      }
    >
      {text}
    </Modal>
  );
}
